import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  ConversationInsert,
  ConversationRow,
  ConversationUpdate,
  MessageInsert,
  MessageRow,
  MessageUpdate,
  PhoneCallInsert,
  PhoneCallRow,
  PhoneCallUpdate,
  NotDeleted,
} from '../types/supabase'
import type { DeepPartialNullable, NotNull } from '../types/util'
import { PURGE } from 'redux-persist'
import { redactObjectExceptKeys } from '../lib/util/redact'
import { replaceOrInsert } from '../lib/util/replaceOrInsert'
import { onSyncDownComplete } from './actions/onSyncDownComplete'

// ============ Conversation Types ============

export type ConversationModel = ConversationRow

export type ConversationInsertPayload = ConversationModel

export type ConversationUpdatePayload = NotNull<NotDeleted<ConversationUpdate>, 'id' | 'updated_at'>

// Assert we can assign a ConversationRow to a ConversationModel
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertConversationRowAssignable: ConversationModel = {} as ConversationRow

// Assert we can assign a ConversationModel to a ConversationInsert
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertConversationRowAssignable2: ConversationInsert = {} as ConversationModel

// Assert we can update a ConversationRow from a ConversationUpdatePayload
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertConversationRowAssignable3: ConversationUpdate = {} as ConversationUpdatePayload

// ============ Message Types ============

export type MessageModel = MessageRow

export type MessageInsertPayload = MessageModel

export type MessageUpdatePayload = NotNull<NotDeleted<MessageUpdate>, 'id' | 'updated_at'>

// Assert we can assign a MessageRow to a MessageModel
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertMessageRowAssignable: MessageModel = {} as MessageRow

// Assert we can assign a MessageModel to a MessageInsert
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertMessageRowAssignable2: MessageInsert = {} as MessageModel

// Assert we can update a MessageRow from a MessageUpdatePayload
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertMessageRowAssignable3: MessageUpdate = {} as MessageUpdatePayload

// ============ PhoneCall Types ============

export type PhoneCallModel = PhoneCallRow

export type PhoneCallInsertPayload = PhoneCallModel

export type PhoneCallUpdatePayload = NotNull<NotDeleted<PhoneCallUpdate>, 'id' | 'updated_at'>

// Assert we can assign a PhoneCallRow to a PhoneCallModel
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertPhoneCallRowAssignable: PhoneCallModel = {} as PhoneCallRow

// Assert we can assign a PhoneCallModel to a PhoneCallInsert
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertPhoneCallRowAssignable2: PhoneCallInsert = {} as PhoneCallModel

// Assert we can update a PhoneCallRow from a PhoneCallUpdatePayload
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertPhoneCallRowAssignable3: PhoneCallUpdate = {} as PhoneCallUpdatePayload

// ============ Slice State ============

export interface ConversationsState {
  conversations: ConversationModel[]
  messages: MessageModel[]
  phoneCalls: PhoneCallModel[]
}

const initialState: ConversationsState = {
  conversations: [],
  messages: [],
  phoneCalls: [],
}

// ============ Slice Definition ============

export const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    // ======== Conversation Actions ========
    addConversation(state, action: PayloadAction<ConversationInsertPayload>) {
      state.conversations ||= []

      const model: ConversationModel = {
        ...action.payload,
      }
      state.conversations.push(model)
    },
    updateConversation(state, action: PayloadAction<ConversationUpdatePayload>) {
      state.conversations ||= []

      const i = state.conversations.findIndex((t) => t.id === action.payload.id)
      if (i < 0) {
        throw new Error(`Could not find conversation with id ${action.payload.id}`)
      }

      // Replace the record with the updated values
      state.conversations[i] = {
        ...state.conversations[i],
        ...action.payload,
      }
    },
    deleteConversation(state, action: PayloadAction<{ id: string; updated_at: string; deleted_at: string }>) {
      state.conversations ||= []

      // Remove it from the conversations array
      const i = state.conversations.findIndex((e) => e.id === action.payload.id)
      if (i >= 0) {
        state.conversations.splice(i, 1)
      }
    },

    // ======== Message Actions ========
    addMessage(state, action: PayloadAction<MessageInsertPayload>) {
      state.messages ||= []

      const model: MessageModel = {
        ...action.payload,
      }
      state.messages.push(model)
    },
    updateMessage(state, action: PayloadAction<MessageUpdatePayload>) {
      state.messages ||= []

      const i = state.messages.findIndex((t) => t.id === action.payload.id)
      if (i < 0) {
        throw new Error(`Could not find message with id ${action.payload.id}`)
      }

      // Replace the record with the updated values
      state.messages[i] = {
        ...state.messages[i],
        ...action.payload,
      }
    },
    deleteMessage(state, action: PayloadAction<{ id: string; updated_at: string; deleted_at: string }>) {
      state.messages ||= []

      // Remove it from the messages array
      const i = state.messages.findIndex((e) => e.id === action.payload.id)
      if (i >= 0) {
        state.messages.splice(i, 1)
      }
    },

    // ======== PhoneCall Actions ========
    addPhoneCall(state, action: PayloadAction<PhoneCallInsertPayload>) {
      state.phoneCalls ||= []

      const model: PhoneCallModel = {
        ...action.payload,
      }
      state.phoneCalls.push(model)
    },
    updatePhoneCall(state, action: PayloadAction<PhoneCallUpdatePayload>) {
      state.phoneCalls ||= []

      const i = state.phoneCalls.findIndex((t) => t.id === action.payload.id)
      if (i < 0) {
        throw new Error(`Could not find phone_call with id ${action.payload.id}`)
      }

      // Replace the record with the updated values
      state.phoneCalls[i] = {
        ...state.phoneCalls[i],
        ...action.payload,
      }
    },
    deletePhoneCall(state, action: PayloadAction<{ id: string; updated_at: string; deleted_at: string }>) {
      state.phoneCalls ||= []

      // Remove it from the phoneCalls array
      const i = state.phoneCalls.findIndex((e) => e.id === action.payload.id)
      if (i >= 0) {
        state.phoneCalls.splice(i, 1)
      }
    },
  },
  extraReducers: (builder) => {
    builder = builder.addCase(PURGE, () => {
      return initialState
    })
    builder = builder.addCase(onSyncDownComplete, (state, action) => {
      state.conversations ||= []
      state.messages ||= []
      state.phoneCalls ||= []

      // ======== Handle conversations ========
      for (const conversation of action.payload.conversations?.updated || []) {
        replaceOrInsert(state.conversations, conversation)
      }

      for (const deletedConversation of action.payload.conversations?.deleted || []) {
        const i = state.conversations.findIndex((t) => t.id === deletedConversation.id)
        if (i >= 0 && state.conversations[i].updated_at <= deletedConversation.updated_at) {
          state.conversations.splice(i, 1)
        }
      }

      // ======== Handle messages ========
      for (const message of action.payload.messages?.updated || []) {
        replaceOrInsert(state.messages, message)
      }

      for (const deletedMessage of action.payload.messages?.deleted || []) {
        const i = state.messages.findIndex((t) => t.id === deletedMessage.id)
        if (i >= 0 && state.messages[i].updated_at <= deletedMessage.updated_at) {
          state.messages.splice(i, 1)
        }
      }

      // ======== Handle phone_calls ========
      for (const phoneCall of action.payload.phone_calls?.updated || []) {
        replaceOrInsert(state.phoneCalls, phoneCall)
      }

      for (const deletedPhoneCall of action.payload.phone_calls?.deleted || []) {
        const i = state.phoneCalls.findIndex((t) => t.id === deletedPhoneCall.id)
        if (i >= 0 && state.phoneCalls[i].updated_at <= deletedPhoneCall.updated_at) {
          state.phoneCalls.splice(i, 1)
        }
      }
    })

    return builder
  },
})

// ============ Action Exports ============

export const {
  addConversation,
  updateConversation,
  deleteConversation,
  addMessage,
  updateMessage,
  deleteMessage,
  addPhoneCall,
  updatePhoneCall,
  deletePhoneCall,
} = conversationsSlice.actions

export type ConversationsSliceAction = ReturnType<typeof conversationsSlice.actions[keyof typeof conversationsSlice.actions]>

export function isConversationsSliceAction(action: any): action is ConversationsSliceAction {
  return action.type?.startsWith(conversationsSlice.name)
}

export default conversationsSlice.reducer

// ============ Redaction for HIPAA Compliance ============

export function redactConversations(state: ConversationsState): DeepPartialNullable<ConversationsState> {
  return {
    conversations: state.conversations.map(redactConversation),
    messages: state.messages.map(redactMessage),
    phoneCalls: state.phoneCalls.map(redactPhoneCall),
  }
}

export function redactConversation(conversation: Partial<ConversationModel>): DeepPartialNullable<ConversationModel> {
  // alice_goal and alice_task_list are intentionally NOT in this list
  // because they may contain PHI (patient names, medical details, etc.)
  return redactObjectExceptKeys(
    conversation,
    'id',
    'created_at',
    'updated_at',
    'membership_id',
    'provider_id',
    'topic_type',
    'topic_ref_id',
    'status',
    'last_message_at',
    'last_message_direction',
    'deleted_at'
  )
}

export function redactMessage(message: Partial<MessageModel>): DeepPartialNullable<MessageModel> {
  // Redact all sensitive content (subject, body, addresses, etc.)
  return redactObjectExceptKeys(
    message,
    'id',
    'created_at',
    'updated_at',
    'conversation_id',
    'direction',
    'status',
    'scheduled_at',
    'sent_at',
    'read_at',
    'deleted_at',
    'membership_id'
  )
}

export function redactPhoneCall(phoneCall: Partial<PhoneCallModel>): DeepPartialNullable<PhoneCallModel> {
  // Redact all sensitive content (phone numbers, transcript, etc.)
  return redactObjectExceptKeys(
    phoneCall,
    'id',
    'created_at',
    'updated_at',
    'conversation_id',
    'status',
    'duration_seconds',
    'requested_at',
    'deleted_at',
    'membership_id'
  )
}

export function redactConversationsSliceAction(action: ConversationsSliceAction) {
  switch (action.type) {
    case addConversation.type:
    case updateConversation.type:
    case deleteConversation.type:
      return {
        type: action.type,
        payload: redactConversation(action.payload),
      }

    case addMessage.type:
    case updateMessage.type:
    case deleteMessage.type:
      return {
        type: action.type,
        payload: redactMessage(action.payload),
      }

    case addPhoneCall.type:
    case updatePhoneCall.type:
    case deletePhoneCall.type:
      return {
        type: action.type,
        payload: redactPhoneCall(action.payload),
      }

    default:
      return {
        type: (action as any).type,
        payload: 'REDACTED',
      }
  }
}
